import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useApolloClient } from "@apollo/client"

import { FAVORITES } from "shared/constants/permalinks"
import { FAVORITE_ACTIONS_OVERLAY } from "shared/context/overlay/types"
import { exists } from "shared/lib/exists"
import { reduceTree } from "shared/lib/treeUtils"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { useTitle } from "shared/lib/usePageTitle"
import { DropHandler } from "shared/types/DragAndDrop"
import { AnchorType } from "shared/types/NavigationAnchor"
import {
  Project,
  ProjectTypename,
  RoomTypename,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { UNASSIGNED_PRODUCTS_TAB_ID } from "entities/project-children/lib/projectTabIDs"
import { readProjectChildFragment } from "entities/project-children/lib/readProjectChildFragment"
import { useFavorites } from "entities/projects/api/useFavorites"
import { useProjectChildrenCache } from "entities/projects/model/useProjectChildrenCache"
import { useProjectRequestMonitor } from "entities/projects/model/useProjectRequestState"
import { fullProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { useAnchor } from "features/navigation/model/useAnchor"
import { useProjectStore } from "features/project-management/model/useProjectStore"
import { useScrollController } from "features/scroll-management/model/useScrollController"

import { FavoritesPageModel } from "../FavoritesPage"
import { FavoritesPageSidebar } from "../sidebar/FavoritesPageSidebar"
import { useSelectedFavorites } from "./useSelectedFavorites"

export function useFavoritesPageModel(): FavoritesPageModel {
  const { t } = useTranslation()
  useScrollController()
  useAnchor(AnchorType.PROJECT)
  useTitle(t("Favorites.Favorites"))

  const { favorites, isLoading, error, updatePosition } = useFavorites()

  useProjectChildrenCache(favorites)

  const {
    selectedElementIDs,
    selectElement,
    collapsedRoomIDs,
    expandedProductPositionIDs,
    collapseRoom,
    expandProductPosition,
    collapseAll,
    expandAll
  } = useProjectStore(FAVORITES)

  const { cache } = useApolloClient()

  const isItemCollapsed = useEvent(
    (item: { id: string; __typename?: string }) => {
      if (item.__typename === RoomTypename) {
        return collapsedRoomIDs.includes(item.id)
      }
      return !expandedProductPositionIDs.includes(item.id)
    }
  )

  const onItemCollapse = useEvent((id: string) => {
    const child = readProjectChildFragment(id, cache)
    if (isProjectRoom(child)) collapseRoom(id)
    else expandProductPosition(id)
  })

  const onCollapseAll = useEvent(() => {
    const roomIDs = reduceTree(
      favorites ?? [],
      (acc: string[], item: { id: string; __typename?: string }) => {
        if (isProjectRoom(item)) acc.push(item.id)
        return acc
      },
      []
    )
    collapseAll(roomIDs)
  })

  const onExpandAll = useEvent(() => {
    const ppIDs = reduceTree(
      favorites ?? [],
      (acc: string[], item: { id: string; __typename?: string }) => {
        if (isProjectProductPosition(item)) acc.push(item.id)
        return acc
      },
      []
    )
    expandAll(ppIDs)
  })

  const { isUpdatingChildren } = useProjectRequestMonitor({
    projectID: FAVORITES_PROJECT_ID
  })

  const selectedFavorites = useSelectedFavorites()

  const handlePositionChange: DropHandler = useEvent(
    (id: string, prev, prevParent, next) => {
      if (exists(next)) {
        return updatePosition({ id, position: next })
      }
      return Promise.resolve()
    }
  )

  const { openOverlay: openFavoriteActionsOverlay } = useOverlay(
    FAVORITE_ACTIONS_OVERLAY
  )

  const handleActions = useEvent(() => {
    if (
      selectedFavorites[0] &&
      isProjectProductPosition(selectedFavorites[0])
    ) {
      openFavoriteActionsOverlay({
        productPosition: selectedFavorites[0]
      })
    }
  })

  const favoritesProject: Project = useMemo(
    () => ({
      __typename: ProjectTypename,
      id: FAVORITES_PROJECT_ID,
      title: t("Favorites.Favorites"),
      position: 0,
      children: favorites,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }),
    [favorites]
  )

  const actionsButtonLabel = useMemo(() => {
    if (isProjectRoom(selectedFavorites[0])) {
      return t("Room.Actions.Actions")
    }
    if (isProjectProductPosition(selectedFavorites[0])) {
      return t("ProductPosition.Actions.Actions")
    }
    return t("Project.Actions.Actions")
  }, [selectedElementIDs, t])

  return {
    projectModel: {
      core: {
        data: {
          project: {
            data: favoritesProject,
            isLoading,
            isUpdating: isUpdatingChildren,
            error: !!error
          },
          permissions: { data: fullProjectPermissions },
          phases: undefined,
          meta: {
            isPending: false,
            href: "#",
            productTotal: 0,
            productPositionTotal: 0
          }
        },
        handlers: {
          onOpen: () => undefined,
          onShowDetails: undefined,
          onEdit: undefined,
          onAddClarificationRequest: undefined,
          onDelete: undefined,
          onActivate: undefined,
          onDuplicate: undefined,
          onShare: undefined,
          onExport: undefined,
          onGaebExport: undefined,
          onMusterheftExport: undefined,
          onOpenProjectHistory: undefined
        }
      },
      team: {
        data: {},
        handlers: {
          onManageTeam: () => undefined,
          onInviteMember: () => undefined,
          onLeaveTeam: () => undefined
        }
      },
      pricing: {
        data: {
          priceLists: undefined,
          standardInflationSurcharge: undefined
        }
      },
      attachments: {
        data: {},
        handlers: { onOpenAttachments: () => undefined }
      },
      creation: {
        handlers: {
          onNewProduct: () => undefined,
          onNewRoom: () => undefined,
          onNewFloor: () => undefined
        }
      },
      clarificationRequest: {
        data: {
          clarificationRequest: null,
          meta: { shouldShowHint: false, lastUpdatedByMember: null }
        },
        handlers: {
          onSubmitContent: () => undefined,
          onDelete: () => undefined,
          onHintClose: () => undefined
        }
      }
    },
    activeTabID: UNASSIGNED_PRODUCTS_TAB_ID,

    selectedElementIDs,
    isItemCollapsed,

    isSortingDisabled: isUpdatingChildren,

    onSelect: selectElement,
    onPositionChange: handlePositionChange,
    onActions: selectedFavorites[0] ? handleActions : undefined,
    actionsButtonLabel,
    onItemCollapse,
    onCollapseAll,
    onExpandAll,

    sidebar: <FavoritesPageSidebar />
  }
}
